import React from 'react';

import { Alert, Box, Chip, Divider, Stack, Typography } from '@mui/material';

import type { CapacityRoomRate } from './utils';
import { groupRoomRatesByRoomType } from './utils';

interface Props {
  roomRates: Array<CapacityRoomRate>;
}

export default function CapacityStep({ roomRates }: Props) {
  if (roomRates.length === 0) {
    return (
      <Alert severity="warning">Select at least one room rate in the previous step before setting capacity.</Alert>
    );
  }

  const roomTypeGroups = groupRoomRatesByRoomType(roomRates);

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        <Typography variant="body2">
          Deal options won&apos;t appear on the front end until their room rate has a capacity set. Default capacity (2
          adults) is applied automatically to every room rate below when you create the Flash offer -- nothing to
          configure here.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          You will still be able to update the capacity anytime afterwards in{' '}
          <strong>Offer Builder → Rates and Inventory → room type → room rate → Capacity</strong>.
        </Typography>
      </Alert>

      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" sx={{ px: 2, py: 1.25, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2">Room types ({roomTypeGroups.length})</Typography>
        </Stack>
        <Divider />
        {roomTypeGroups.map((group) => (
          <Box key={group.roomTypeId}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1 }} gap={2}>
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant="body2">{group.roomName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.ratePlanNames.length > 0
                    ? group.ratePlanNames.join(', ')
                    : `${group.roomRates.length} rate plan${group.roomRates.length === 1 ? '' : 's'}`}
                </Typography>
              </Stack>
              <Chip label="Will default to 2A" size="small" variant="outlined" />
            </Stack>
            <Divider />
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
